import { useEffect, useState } from "react";
import axios from "axios";
import { Hammer, Share2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * QueuePositionWidget
 * --------------------
 * Buyer-facing scarcity widget on the Checkout Success page — shows them
 * where they sit in the current dispatch queue so the confirmation feels
 * like they earned a spot, not just completed a form.
 */
const QueuePositionWidget = ({ orderId }) => {
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!orderId) return;
    let mounted = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/stats/queue-position/${orderId}`);
        if (mounted) setData(data);
      } catch (e) {
        // Widget is decorative — don't break the success page
        console.warn("queue-position unavailable:", e?.message || e);
      }
    })();
    return () => { mounted = false; };
  }, [orderId]);

  if (!data) return null;

  const { position, capacity, dispatch_month } = data;
  const progress = Math.min(100, Math.round((position / Math.max(capacity, 1)) * 100));

  const share = async () => {
    const text = `Just ordered a custom enclosure from @repticraft — I'm build #${position} of ${capacity} in the ${dispatch_month} dispatch slot 🛠`;
    try {
      if (navigator.share) {
        await navigator.share({ title: "ReptiCraft build", text });
      } else {
        await navigator.clipboard.writeText(text);
        toast.success("Copied to clipboard");
      }
    } catch {
      // user cancelled share — ignore
    }
  };

  return (
    <div
      className="mt-8 text-left bg-[#121212] border border-[#E07A5F]/30 p-5"
      data-testid="queue-position-widget"
    >
      <div className="flex items-center gap-2 mb-3">
        <Hammer className="w-4 h-4 text-[#E07A5F]" />
        <p className="text-[10px] uppercase tracking-widest text-[#E07A5F] font-semibold">
          You're in the {dispatch_month} dispatch slot
        </p>
      </div>
      <p className="text-xl font-bold text-[#F4F1DE] leading-tight mb-3" data-testid="queue-position-headline">
        Build{" "}
        <span className="text-[#E07A5F]">#{position}</span>{" "}
        <span className="text-[#F4F1DE]/40 font-normal text-base">of {capacity}</span>
      </p>

      {/* Progress bar */}
      <div className="relative h-2 bg-[#0A0A0A] overflow-hidden mb-4" data-testid="queue-progress">
        <div
          className="absolute inset-y-0 left-0 bg-gradient-to-r from-[#E07A5F] via-[#F5B945] to-[#E07A5F] transition-all duration-700"
          style={{ width: `${progress}%` }}
        />
      </div>

      <p className="text-xs text-[#F4F1DE]/50 leading-relaxed">
        Your frame is next in line. Expect Day 1 / Day 3 / Day 5 updates as your build moves
        through the workshop — we'll email you each step.
      </p>

      <button
        onClick={share}
        className="mt-4 inline-flex items-center gap-1.5 px-3 py-1.5 border border-white/20 text-xs uppercase tracking-widest text-[#F4F1DE]/80 hover:border-[#E07A5F]/60 hover:text-[#E07A5F]"
        data-testid="share-build-btn"
      >
        <Share2 className="w-3 h-3" />
        Share your spot
      </button>
    </div>
  );
};

export default QueuePositionWidget;
